import React, { useEffect, useRef, useState } from 'react';
import { Pressable, type PressableProps } from 'react-native';
// Animated.View puro, não o AnimatedView do NativeWind: o cssInterop descarta o
// estilo animado no nativo e o trilho ficava sem cor (switch invisível).
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { cn } from '@/utils/cn';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { themeColors } from '@/hooks/use-theme-colors';

export interface SwitchProps extends Omit<PressableProps, 'children'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  disabled?: boolean;
}

const TRACK_WIDTH = 48;
const TRACK_HEIGHT = 28;
const THUMB_SIZE = 22;
const PADDING = 3;
const TRAVEL = TRACK_WIDTH - THUMB_SIZE - PADDING * 2;

const TRACK_OFF_LIGHT = '#D4D4D4'; // neutral-300: contrasta com o card branco e com o thumb
const TRACK_OFF_DARK = themeColors('dark').neutral['300'];
const TRACK_ON = '#FD5001'; // primary

/**
 * Switch controlado. O thumb é branco com sombra (visível nos dois estados) e
 * a transição é feita no Reanimated — `transition-*` do Tailwind não existe no RN.
 * O toque vira na hora (estado local) e anima no UI thread; `checked` do pai
 * continua mandando e corrige o estado se a mudança falhar.
 */
export function Switch({
  checked = false,
  onCheckedChange,
  className,
  disabled,
  ...props
}: SwitchProps) {
  const isDark = useColorScheme() === 'dark';
  const [on, setOn] = useState(checked);
  const progress = useSharedValue(checked ? 1 : 0);
  const checkedRef = useRef(checked);
  checkedRef.current = checked;

  useEffect(() => {
    setOn(checked);
    progress.value = withTiming(checked ? 1 : 0, { duration: 160 });
  }, [checked, progress]);

  const handlePress = () => {
    if (disabled) return;
    const next = !on;
    // Anima no toque, sem esperar o pai re-renderizar a tela.
    setOn(next);
    progress.value = withTiming(next ? 1 : 0, { duration: 160 });
    onCheckedChange?.(next);
    // Pai recusou sem mexer em `checked` (ex.: validação antes do mutate):
    // o effect não dispara, então volta aqui.
    setTimeout(() => {
      if (checkedRef.current === next) return;
      setOn(checkedRef.current);
      progress.value = withTiming(checkedRef.current ? 1 : 0, { duration: 160 });
    }, 600);
  };

  const TRACK_OFF = isDark ? TRACK_OFF_DARK : TRACK_OFF_LIGHT;
  const trackStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, [0, 1], [TRACK_OFF, TRACK_ON]),
  }));

  const thumbStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: progress.value * TRAVEL }],
  }));

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      hitSlop={8}
      accessibilityRole="switch"
      accessibilityState={{ checked: on, disabled: !!disabled }}
      className={cn('shrink-0', disabled ? 'opacity-50' : '', className)}
      {...props}
    >
      <Animated.View
        style={[
          {
            width: TRACK_WIDTH,
            height: TRACK_HEIGHT,
            borderRadius: TRACK_HEIGHT / 2,
            padding: PADDING,
            justifyContent: 'center',
            // Cor estática por baixo da animada: o trilho nunca fica transparente.
            backgroundColor: on ? TRACK_ON : TRACK_OFF,
          },
          trackStyle,
        ]}
      >
        <Animated.View
          style={[
            {
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              borderRadius: THUMB_SIZE / 2,
              backgroundColor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(23, 17, 10, 0.25)',
            },
            thumbStyle,
          ]}
        />
      </Animated.View>
    </Pressable>
  );
}
